import React from 'react';
import './TermsOfService.css';

const TermsOfService = () => {
  return (
    <div className="terms-container">

      {/* ── SECTION 1: FOOD SAFETY POLICY ── */}
      <h1 className="terms-title">HOMECOOKT – Food Safety Policy</h1>
      <p className="effective-date">Effective Date: June 1, 2026</p>

      <section className="terms-section">
        <h2 className="section-title">1. Purpose and Scope</h2>
        <div className="section-content">
          <p>This Food Safety Policy governs all food prepared, listed, sold, or delivered through the HomeCookt platform ("Platform"). It applies to all users including home cooks ("Cooks"), customers ("Users"), and any third-party facilitators.</p>
          <p>HomeCookt is a technology platform connecting independent home cooks with customers and does not itself prepare, handle, store, or deliver food.</p>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">2. Regulatory Compliance</h2>
        <div className="section-content">
          <p>All home cooks must comply with:</p>
          <ul>
            <li>Applicable national and local food safety laws</li>
            <li>In India, where applicable, Food Safety and Standards Authority of India (FSSAI) regulations and licensing requirements</li>
            <li>Hygiene and food handling standards prescribed by local authorities</li>
          </ul>
          <p>Cooks are solely responsible for determining whether their activity requires registration, license, or approval.</p>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">3. Independent Food Business Status</h2>
        <div className="section-content">
          <p>Each home cook operates as an independent food business operator (FBO) or equivalent under applicable law.</p>
          <p>HomeCookt:</p>
          <ul>
            <li>Does not act as a food business operator</li>
            <li>Does not certify kitchens or food safety compliance</li>
            <li>Does not guarantee quality, safety, or suitability of any food</li>
          </ul>
          <h3 className="section-subtitle">3.1 Marketplace Disclaimer</h3>
          <p>HomeCookt simply acts as a marketplace platform showcasing available food offerings. HomeCookt does not assume responsibility or liability for any impact, positive or negative, arising from food safety deficiencies, hygiene failures, preparation issues, contamination, or health-related consequences associated with food supplied by independent cooks.</p>
          <p>This includes, without limitation, foodborne illness, waterborne illness, respiratory infections, airborne diseases, contact-based communicable diseases, allergic reactions, contamination events, and any condition that may reasonably be considered abnormal from the highest standards of physical health, safety, wellbeing, or public health.</p>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">4. Mandatory Hygiene Standards</h2>
        <div className="section-content">
          <p>All home cooks must ensure:</p>
          <ul>
            <li>Clean and sanitary food preparation environment</li>
            <li>Regular cleaning of utensils, surfaces, and storage areas</li>
            <li>Use of safe potable water</li>
            <li>Effective pest control measures</li>
            <li>Prevention of cross-contamination between raw and cooked foods</li>
          </ul>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">5. Food Handling Requirements</h2>
        <div className="section-content">
          <p>Cooks must:</p>
          <ul>
            <li>Follow safe cooking temperatures for all food types</li>
            <li>Use fresh and unexpired ingredients</li>
            <li>Store food at safe temperatures prior to delivery</li>
            <li>Ensure hygienic packaging prior to handover</li>
            <li>Avoid preparation of food in unsafe or contaminated conditions</li>
          </ul>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">6. Medical Fitness &amp; Personal Hygiene</h2>
        <div className="section-content">
          <p>Cooks must not prepare food if they:</p>
          <ul>
            <li>Are suffering from infectious diseases or symptoms (including fever, vomiting, diarrhea, respiratory infection)</li>
            <li>Have been advised by a medical professional to avoid food handling</li>
          </ul>
          <p>Cooks must maintain personal hygiene, including clean clothing and safe handling practices.</p>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">7. Allergen Disclosure (Strict Requirement)</h2>
        <div className="section-content">
          <p>Cooks must provide full and accurate disclosure of:</p>
          <ul>
            <li>All ingredients used</li>
            <li>Known allergens including but not limited to: peanuts, tree nuts, milk, eggs, gluten, soy, fish, shellfish, and sesame</li>
          </ul>
          <p>Where cross-contamination risk exists, it must be clearly disclosed.</p>
          <p>Failure to disclose allergens may result in suspension or termination.</p>
          <p>Cooks assume full responsibility and liability for any injury, illness, allergic reaction, health consequence, loss, damage, claim, or legal action resulting from non-disclosure, inaccurate disclosure, unsafe food sourcing, improper storage practices, inadequate preparation methods, contamination, or the supply of food that fails to meet acceptable safety and hygiene standards.</p>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">8. Packaging &amp; Transport Safety</h2>
        <div className="section-content">
          <ul>
            <li>Food must be packaged in food-grade, hygienic packaging</li>
            <li>Packaging must prevent contamination during transport</li>
            <li>Hot and cold foods must be separated where applicable</li>
            <li>Sealing of food is strongly recommended</li>
          </ul>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">9. Traceability &amp; Records</h2>
        <div className="section-content">
          <p>HomeCookt may require cooks to maintain basic records of:</p>
          <ul>
            <li>Ingredient sourcing</li>
            <li>Preparation date/time</li>
            <li>Batch or order reference</li>
          </ul>
          <p>This may be used for safety investigations.</p>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">10. Enforcement</h2>
        <div className="section-content">
          <p>HomeCookt reserves the right to:</p>
          <ul>
            <li>Remove listings</li>
            <li>Suspend or terminate accounts</li>
            <li>Restrict access to the platform</li>
            <li>Report serious violations to regulatory authorities</li>
          </ul>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">11. Assumption of Risk</h2>
        <div className="section-content">
          <p>Users acknowledge that food is prepared in home environments and may carry inherent risks.</p>
          <p>Customers voluntarily consume food at their own discretion, subject to applicable consumer protection laws.</p>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">12. Limitation of Liability</h2>
        <div className="section-content">
          <p>To the maximum extent permitted by law:</p>
          <ul>
            <li>HomeCookt is not liable for foodborne illness, waterborne illness, contamination, allergic reactions, communicable diseases, respiratory infections, airborne diseases, contact-based infections, or damages arising directly or indirectly from the preparation, handling, storage, transportation, purchase, sale, delivery, or consumption of food offered through the Platform.</li>
            <li>Liability remains solely with the individual cook preparing and supplying the food.</li>
          </ul>
        </div>
      </section>

      {/* ── DIVIDER ── */}
      <div className="policy-divider" />

      {/* ── SECTION 2: TERMS & CONDITIONS ── */}
      <h1 className="terms-title">HOMECOOKT – Terms &amp; Conditions</h1>
      <p className="effective-date">Effective Date: June 1, 2026</p>

      <p className="terms-intro">
        These Terms &amp; Conditions ("Terms") govern access to and use of the HomeCookt platform, including website, mobile applications, APIs, and related services. By accessing or using HomeCookt, users agree to be bound by these Terms.
      </p>

      <section className="terms-section">
        <h2 className="section-title">1. Introduction</h2>
        <div className="section-content">
          <p>These Terms &amp; Conditions ("Terms") govern access to and use of the HomeCookt platform, including website, mobile applications, APIs, and related services.</p>
          <p>By accessing or using HomeCookt, users agree to be bound by these Terms.</p>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">2. Platform Structure</h2>
        <div className="section-content">
          <p>HomeCookt is a technology marketplace platform that:</p>
          <ul>
            <li>Connects independent home cooks with customers</li>
            <li>Facilitates discovery, ordering, and payments</li>
            <li>Does not prepare, sell, store, or deliver food</li>
          </ul>
          <p>HomeCookt is not a restaurant, food business, or catering service.</p>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">3. Eligibility</h2>
        <div className="section-content">
          <p>Users must:</p>
          <ul>
            <li>Be legally capable of entering binding contracts</li>
            <li>Comply with local laws in their jurisdiction</li>
          </ul>
          <p>Home cooks must additionally comply with all applicable food safety and business regulations.</p>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">4. Account Responsibilities</h2>
        <div className="section-content">
          <p>Users are responsible for:</p>
          <ul>
            <li>Maintaining account confidentiality</li>
            <li>Ensuring accuracy of provided information</li>
            <li>All activity under their account</li>
          </ul>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">5. Role of Home Cooks</h2>
        <div className="section-content">
          <p>Home cooks are independent contractors and are solely responsible for:</p>
          <ul>
            <li>Food preparation</li>
            <li>Hygiene and safety compliance</li>
            <li>Ingredient accuracy</li>
            <li>Regulatory compliance</li>
            <li>Order fulfilment</li>
          </ul>
          <p>No employment, partnership, or agency relationship exists between HomeCookt and cooks.</p>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">6. Customer Responsibilities</h2>
        <div className="section-content">
          <p>Customers must:</p>
          <ul>
            <li>Review ingredients and allergen information before ordering</li>
            <li>Ensure suitability of food for their dietary needs</li>
            <li>Use personal judgment when consuming home-prepared food</li>
          </ul>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">7. Ordering &amp; Contract Formation</h2>
        <div className="section-content">
          <ul>
            <li>Orders constitute a direct agreement between customer and cook</li>
            <li>HomeCookt is not a party to the food preparation contract</li>
            <li>Acceptance of orders is subject to cook availability</li>
          </ul>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">8. Payments</h2>
        <div className="section-content">
          <p>HomeCookt may:</p>
          <ul>
            <li>Process payments on behalf of users</li>
            <li>Charge service fees, commissions, or platform fees</li>
          </ul>
          <p>All pricing and fees will be disclosed prior to purchase.</p>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">9. Cancellations &amp; Refunds</h2>
        <div className="section-content">
          <p>Policies may vary by region and seller. HomeCookt may intervene in disputes but does not guarantee refunds unless required by law or platform policy.</p>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">10. Ratings, Reviews, and Content</h2>
        <div className="section-content">
          <p>Users may submit ratings and reviews provided they:</p>
          <ul>
            <li>Are truthful and lawful</li>
            <li>Do not contain abusive or misleading content</li>
          </ul>
          <p>HomeCookt reserves the right to moderate or remove content.</p>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">11. Prohibited Conduct</h2>
        <div className="section-content">
          <p>Users must not:</p>
          <ul>
            <li>Misrepresent food, ingredients, or identity</li>
            <li>Upload unsafe or contaminated food listings</li>
            <li>Violate food safety laws</li>
            <li>Engage in fraud or abusive behaviour</li>
            <li>Circumvent platform payments</li>
          </ul>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">12. Intellectual Property</h2>
        <div className="section-content">
          <p>All platform content, branding, software, and design elements remain the intellectual property of HomeCookt or its licensors.</p>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">13. Data Protection &amp; Privacy</h2>
        <div className="section-content">
          <p>User data will be processed in accordance with applicable privacy laws, including:</p>
          <ul>
            <li>India Digital Personal Data Protection Act (where applicable)</li>
            <li>GDPR (for EU users)</li>
            <li>Other regional equivalents as the platform expands</li>
          </ul>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">14. Limitation of Liability</h2>
        <div className="section-content">
          <p>To the maximum extent permitted by law:</p>
          <ul>
            <li>HomeCookt provides services on an "as is" basis</li>
            <li>HomeCookt disclaims liability for indirect, incidental, or consequential damages</li>
            <li>Liability for food-related harm lies with the cook, not the platform</li>
          </ul>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">15. Indemnity</h2>
        <div className="section-content">
          <p>Users agree to indemnify and hold harmless HomeCookt against claims arising from:</p>
          <ul>
            <li>Food safety violations</li>
            <li>Misrepresentation</li>
            <li>Breach of law or platform rules</li>
          </ul>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">16. Suspension &amp; Termination</h2>
        <div className="section-content">
          <p>HomeCookt may suspend or terminate accounts for:</p>
          <ul>
            <li>Safety violations</li>
            <li>Fraudulent activity</li>
            <li>Legal non-compliance</li>
            <li>Harmful conduct toward users or platform integrity</li>
          </ul>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">17. Changes to Terms</h2>
        <div className="section-content">
          <p>HomeCookt may update these Terms periodically. Continued use constitutes acceptance of changes.</p>
        </div>
      </section>

      <section className="terms-section">
        <h2 className="section-title">18. Governing Law</h2>
        <div className="section-content">
          <p>Initial governing law: India (subject to jurisdiction selection upon incorporation).</p>
          <p>Future versions may include multi-jurisdiction frameworks for global operations.</p>
        </div>
      </section>

    </div>
  );
};

export default TermsOfService;
